import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { SessionProvider, useSession } from './lib/session.js';
import { useBotStatus } from './lib/bot-status.js';
import { SignIn } from './pages/SignIn.js';
import { Shell } from './components/Shell.js';
import { Pairs } from './pages/Pairs.js';
import { PairDetail } from './pages/PairDetail.js';
import { Watchlist } from './pages/Watchlist.js';
import { Portfolio } from './pages/Portfolio.js';
import { BotStatus } from './pages/BotStatus.js';
import { System } from './pages/System.js';
import { TradeIntents } from './pages/TradeIntents.js';
import { AuditLog } from './pages/AuditLog.js';
import './styles/app.css';

/**
 * Application routing.
 *
 * Authentication gates the entire router rather than individual routes. An
 * anonymous visitor sees the sign-in screen and nothing else — there is no
 * route, and therefore no accidentally-unguarded route, behind it.
 *
 * The client-side guard is a convenience, never the control. Every API endpoint
 * enforces the session independently, so a hand-edited URL reaches an empty
 * shell and a 401, not data.
 *
 * `useBotStatus` is called exactly once, here. Calling it again lower down would
 * start a second polling loop against its own copy of the state, so the shell's
 * interlock lamps and the bot page could disagree about whether the system can
 * spend money. The status and its refresh callback are passed down instead.
 */

function AuthenticatedApp() {
  const { status: botStatus, stale, refresh } = useBotStatus();

  return (
    <Routes>
      <Route element={<Shell status={botStatus} stale={stale} />}>
        <Route path="/pairs" element={<Pairs />} />
        <Route path="/pairs/:id" element={<PairDetail />} />
        <Route path="/watchlist" element={<Watchlist />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/intents" element={<TradeIntents />} />
        <Route path="/audit" element={<AuditLog />} />
        <Route
          path="/bot"
          element={<BotStatus status={botStatus} stale={stale} onRefresh={refresh} />}
        />
        <Route path="/system" element={<System />} />
        <Route path="*" element={<Navigate to="/pairs" replace />} />
      </Route>
    </Routes>
  );
}

function Router() {
  const { status } = useSession();

  if (status === 'loading') {
    // The frame that is about to be there, rather than a spinner that flashes.
    return <div className="app-frame" aria-busy="true" />;
  }

  if (status !== 'authenticated') {
    return <SignIn />;
  }

  return <AuthenticatedApp />;
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Router />
      </SessionProvider>
    </BrowserRouter>
  );
}
